/**
 * API client for item ratings (like/dislike on questions and AI feedback).
 *
 * @module ratingsApi
 */

import { apiCall, apiGet, apiPost } from '@/lib/apiUtils';
import type { RatingModule, RatingTargetType, RatingValue } from '@/lib/ratingConstants';

export interface RatingDetail {
  readonly id: string;
  readonly targetType: RatingTargetType;
  readonly targetId: string;
  readonly module: RatingModule;
  readonly rating: RatingValue;
  readonly reasonCodes?: string[] | null;
  readonly freeText?: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface UpsertRatingPayload {
  readonly targetType: RatingTargetType;
  readonly targetId: string;
  readonly module: RatingModule;
  readonly rating: RatingValue;
  readonly reasonCodes?: string[];
  readonly freeText?: string;
}

export interface UpsertRatingResponse {
  readonly id: string;
  readonly rating: RatingValue;
  readonly offerHumanReview: boolean;
}

/** Create or update a rating. Throws on error so callers can reconcile optimistic UI. */
export async function postRating(
  payload: UpsertRatingPayload,
): Promise<UpsertRatingResponse | null> {
  return apiPost<UpsertRatingResponse>('/ratings', payload, {
    errorMessage: 'Failed to save rating',
    context: 'postRating',
  });
}

/** Batch-fetch the current user's ratings for a set of target ids. */
export async function getRatingsForTargets(targetIds: string[]): Promise<RatingDetail[]> {
  if (targetIds.length === 0) return [];
  const query = encodeURIComponent(targetIds.join(','));
  const result = await apiGet<RatingDetail[]>(`/ratings?targetIds=${query}`, {
    errorMessage: 'Failed to load ratings',
    context: 'getRatingsForTargets',
  });
  return result ?? [];
}

/** Remove a rating (untoggle). Returns true on success. */
export async function deleteRating(ratingId: string): Promise<boolean> {
  const result = await apiCall<boolean>(`/ratings/${ratingId}`, {
    method: 'DELETE',
    parseJson: false,
    errorMessage: 'Failed to remove rating',
    context: 'deleteRating',
    errorReturnValue: false,
  });
  return result !== false;
}
